import {
  fetchAppointmentById,
  fetchAppointmentByShipmentNumber,
} from "../Appointments/TabContent/api/AppointmentsApi";
import {
  fetchShipmentPartialSearch,
  fetchYmsGateDetails,
  getOfficeCode,
  lookupGateCheckIn,
} from "./api";
import { mapGateDetailsToAppointment, mapGateCheckInLookupResponse } from "./checkInGateUtils";

const CHECK_IN_TYPEAHEAD_MIN_LEN = 2;
const CHECK_IN_TYPEAHEAD_PAGE_SIZE = 8;

export { CHECK_IN_TYPEAHEAD_MIN_LEN, CHECK_IN_TYPEAHEAD_PAGE_SIZE };

// A trailer can only gate-check-in against a shipment that's actually moving: the carrier
// has accepted the tender ("Accepted") or it's already "In Transit". Unassigned/New shipments
// have no carrier yet, and Recalled/Rejected/Cancelled are dead — none are check-in candidates.
const CHECK_IN_ELIGIBLE_STATUSES = new Set(["accepted", "in transit"]);

/** Normalize partial-search rows for check-in type-ahead / disambiguation lists. */
export function mapPartialSearchToCheckInSuggestions(data) {
  const rows = Array.isArray(data?.content)
    ? data.content
    : Array.isArray(data)
      ? data
      : [];
  const seen = new Set();
  const out = [];
  for (const row of rows) {
    const shipmentNumber = String(row?.shipmentNumber ?? "").trim();
    if (!shipmentNumber || seen.has(shipmentNumber.toLowerCase())) continue;
    const status = String(row?.status ?? "").trim();
    if (!CHECK_IN_ELIGIBLE_STATUSES.has(status.toLowerCase())) continue;
    seen.add(shipmentNumber.toLowerCase());
    out.push({
      shipmentNumber,
      carrierName: String(row?.carrierName ?? "").trim(),
      status,
      officeCode: String(row?.officeCode ?? "").trim(),
    });
  }
  return out;
}

/** Type-ahead: partial shipment # matches for yard check-in search. */
export async function searchCheckInShipmentSuggestions(rawQuery, officeCodeOverride) {
  const q = String(rawQuery ?? "").trim();
  if (q.length < CHECK_IN_TYPEAHEAD_MIN_LEN) return [];

  const officeCode = String(officeCodeOverride ?? getOfficeCode() ?? "").trim();
  try {
    const data = await fetchShipmentPartialSearch(q, officeCode, {
      page: 0,
      size: CHECK_IN_TYPEAHEAD_PAGE_SIZE,
    });
    return mapPartialSearchToCheckInSuggestions(data);
  } catch {
    return [];
  }
}

function pickLatestAppointment(data) {
  if (!data) return null;
  if (!Array.isArray(data)) return data;
  return (
    data
      .filter((a) => !a?.cancelled)
      .sort(
        (a, b) =>
          new Date(b?.modifiedAt || b?.createdAt || 0) -
          new Date(a?.modifiedAt || a?.createdAt || 0)
      )[0] || null
  );
}

function attachEligibility(appointment, eligibility) {
  if (!appointment || !eligibility) return appointment;
  return { ...appointment, _gateEligibility: eligibility };
}

/**
 * Resolve an appointment for gate check-in: gate details id, numeric appointment id, or shipment #.
 * Prefers GET /api/trailers/gate-check-in/lookup when `officeCode` is available.
 */
export async function lookupAppointmentForCheckIn(rawQuery, officeCodeOverride) {
  const q = rawQuery.trim();
  if (!q) return null;

  const officeCode = String(officeCodeOverride ?? getOfficeCode() ?? "").trim();

  if (officeCode) {
    try {
      const params =
        /^\d+$/.test(q)
          ? { appointmentId: q, officeCode }
          : { shipmentNumber: q, officeCode };

      const raw = await lookupGateCheckIn(params);
      const { appointment, eligibility } = mapGateCheckInLookupResponse(raw);
      if (appointment?.appointmentId) {
        return attachEligibility(appointment, eligibility);
      }
    } catch (e) {
      const st = e?.response?.status;
      if (st === 403) {
        const err = new Error("YMS_GATE_LOOKUP_FORBIDDEN");
        err.cause = e;
        err.response = e.response;
        throw err;
      }
      /* fall through to legacy paths */
    }
  }

  try {
    const rawGate = await fetchYmsGateDetails(q);
    const mapped = mapGateDetailsToAppointment(rawGate);
    if (mapped?.appointmentId) return mapped;
  } catch {
    /* try fallbacks */
  }

  if (/^\d+$/.test(q)) {
    try {
      const data = await fetchAppointmentById(q);
      const row = Array.isArray(data) ? pickLatestAppointment(data) : data;
      if (row?.appointmentId) return row;
    } catch {
      /* continue */
    }
  }

  try {
    const byShip = await fetchAppointmentByShipmentNumber(q);
    const row = pickLatestAppointment(byShip);
    if (row?.appointmentId) return row;
  } catch {
    /* */
  }

  return null;
}

/**
 * When exact lookup fails, resolve via partial shipment search (single match auto-picks).
 * Returns { appointment, suggestions } — suggestions set when multiple matches need user pick.
 */
export async function lookupAppointmentForCheckInWithPartialFallback(
  rawQuery,
  officeCodeOverride
) {
  const q = String(rawQuery ?? "").trim();
  if (!q) return { appointment: null, suggestions: [] };

  const direct = await lookupAppointmentForCheckIn(q, officeCodeOverride);
  if (direct?.appointmentId) {
    return { appointment: direct, suggestions: [] };
  }

  const suggestions = await searchCheckInShipmentSuggestions(q, officeCodeOverride);
  if (suggestions.length === 1) {
    const appointment = await lookupAppointmentForCheckIn(
      suggestions[0].shipmentNumber,
      officeCodeOverride
    );
    return { appointment, suggestions: appointment ? [] : suggestions };
  }

  return { appointment: null, suggestions };
}
